import { useState } from 'preact/hooks';
import { useApi } from '../../hooks/useApi.js';
import { StatCard } from '../../components/StatCard.jsx';
import { DeltaBadge } from '../../components/DeltaBadge.jsx';
import { LineChart } from '../../components/LineChart.jsx';
import { BarChart } from '../../components/BarChart.jsx';

const INK = '#34C77B';

// Honest async state: spinner-text while loading, the real error otherwise.
const Loading = () => <p class="py-6 text-center text-sm text-text-muted">Loading…</p>;
const Failed = ({ error }) => (
  <p class="py-6 text-center text-sm text-text-muted">
    Couldn’t load — {error?.message || 'request failed'}
    {error?.status ? ` (${error.status})` : ''}.
  </p>
);

const RANGES = [
  { v: 'this_week', label: 'Week' },
  { v: 'this_month', label: 'Month' },
  { v: 'this_quarter', label: 'Quarter' },
  { v: 'this_year', label: 'Year' },
  { v: 'custom', label: 'Custom' },
];

// ── Windowed KPI card: value + period-vs-period delta ────────────────────────
const Kpi = ({ label, kpi, invert = false, suffix }) => (
  <div class="relative overflow-hidden rounded-sm border border-border-subtle bg-bg-card p-4">
    <span class="absolute inset-y-0 left-0 w-[3px]" style={{ background: INK }} aria-hidden="true" />
    <p class="label mb-2">{label}</p>
    <div class="flex items-end justify-between gap-2">
      <p class="m-0 font-serif text-[30px] font-medium leading-none tracking-tighter text-text-primary">
        {kpi?.value ?? '—'}{suffix && <span class="ml-0.5 text-sm text-text-muted">{suffix}</span>}
      </p>
      {kpi && <DeltaBadge pct={kpi.deltaPct} invert={invert} />}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section class="mb-8">
    <h2 class="m-0 mb-3 font-serif text-xl font-medium text-text-primary">{title}</h2>
    {children}
  </section>
);

const Card = ({ title, children }) => (
  <div class="rounded-md border border-border-subtle bg-bg-card p-4">
    {title && <p class="label mb-3">{title}</p>}
    {children}
  </div>
);

// ── Two-series trend (e.g. issues opened vs resolved) ────────────────────────
const TrendCard = ({ title, qs, metrics }) => {
  // metrics: [{ metric, color, label }]
  const a = useApi(`/statistics/timeseries?${qs}&metric=${metrics[0].metric}`);
  const b = useApi(metrics[1] ? `/statistics/timeseries?${qs}&metric=${metrics[1].metric}` : null);
  const error = a.error || b.error;
  const series = [];
  if (a.data) series.push({ points: a.data.points, color: metrics[0].color, label: metrics[0].label });
  if (metrics[1] && b.data) series.push({ points: b.data.points, color: metrics[1].color, label: metrics[1].label });
  return (
    <Card title={title}>
      {error ? <Failed error={error} /> : series.length ? <LineChart series={series} /> : <Loading />}
    </Card>
  );
};

const BreakdownCard = ({ title, qs, dimension, metric = 'issues', accent = INK, format }) => {
  const { data, error } = useApi(`/statistics/breakdown?${qs}&dimension=${dimension}&metric=${metric}`);
  return (
    <Card title={title}>
      {error ? <Failed error={error} /> : data ? <BarChart data={data.items} accent={accent} formatValue={format} /> : <Loading />}
    </Card>
  );
};

const RecurringCard = ({ qs }) => {
  const { data, error } = useApi(`/statistics/recurring-issues?${qs}`);
  return (
    <Card title="Recurring issues (same machine + title)">
      {error ? (
        <Failed error={error} />
      ) : !data ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <p class="py-6 text-center text-sm text-text-muted">No repeats in this period — good sign.</p>
      ) : (
        <ul class="m-0 list-none p-0">
          {data.items.map((r) => (
            <li key={`${r.machine}-${r.title}`} class="flex items-center justify-between border-b border-border-subtle py-2 last:border-0">
              <span class="text-sm text-text-body">{r.title}</span>
              <span class="flex items-center gap-2 text-xs text-text-muted">
                {r.machine}
                <span class="rounded-sm bg-bg-base px-1.5 py-0.5 font-mono text-ink-magenta">×{r.count}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// ── Entity-vs-entity comparison ──────────────────────────────────────────────
const COMPARE_OPTIONS = [
  { dimension: 'printer', metric: 'issues', label: 'Issues by printer', accent: '#E63B7A' },
  { dimension: 'printer', metric: 'consumption', label: 'Consumption by printer', accent: '#F4C430' },
  { dimension: 'printer', metric: 'mixes', label: 'Mixes by printer', accent: '#00B4D8' },
  { dimension: 'severity', metric: 'issues', label: 'Issues by severity', accent: '#E63B7A' },
  { dimension: 'issue_category', metric: 'issues', label: 'Issues by category', accent: '#A18AF5' },
  { dimension: 'print_direction', metric: 'mixes', label: 'Mixes by direction', accent: '#00B4D8' },
  { dimension: 'customer', metric: 'inks', label: 'Inks by customer', accent: '#FF8A4C' },
];

const ComparePanel = ({ qs }) => {
  const [idx, setIdx] = useState(0);
  const opt = COMPARE_OPTIONS[idx];
  const { data, error } = useApi(`/statistics/breakdown?${qs}&dimension=${opt.dimension}&metric=${opt.metric}`);
  return (
    <Card title="Compare">
      <select
        class="mb-4 w-full max-w-xs rounded-md border border-border-strong bg-bg-base px-3 py-2 text-sm text-text-body"
        value={idx}
        onChange={(e) => setIdx(Number(e.currentTarget.value))}
      >
        {COMPARE_OPTIONS.map((o, i) => <option key={o.label} value={i}>{o.label}</option>)}
      </select>
      {error ? <Failed error={error} /> : data ? <BarChart data={data.items} accent={opt.accent} /> : <Loading />}
    </Card>
  );
};

export const StatisticsPage = () => {
  const [range, setRange] = useState('this_month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const custom = range === 'custom';
  const ready = !custom || (from && to);
  const qs = custom ? `range=custom&from=${from}&to=${to}` : `range=${range}`;

  const { data: ov, error: ovError } = useApi(ready ? `/statistics/overview?${qs}` : null);
  const w = ov?.windowed;
  const s = ov?.scalar;

  return (
    <div>
      <section class="mb-6">
        <div class="mb-1.5 flex items-center gap-2">
          <span class="block h-2.5 w-2.5 rounded-full" style={{ background: INK }} />
          <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: INK }}>Statistics</span>
        </div>
        <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Trends &amp; comparisons</h1>
        <p class="m-0 text-sm text-text-secondary">Everything the suite tracks — issues, maintenance, inventory, and ink matches — over time.</p>
      </section>

      {/* Range controls */}
      <div class="mb-6 flex flex-wrap items-center gap-3">
        <div class="flex overflow-hidden rounded-md border border-border-strong">
          {RANGES.map((r) => (
            <button
              key={r.v}
              onClick={() => setRange(r.v)}
              class={`px-3 py-1.5 text-sm ${range === r.v ? 'text-bg-base' : 'text-text-secondary hover:text-text-primary'}`}
              style={range === r.v ? { background: INK } : undefined}
            >
              {r.label}
            </button>
          ))}
        </div>
        {custom && (
          <div class="flex items-center gap-2">
            <input type="date" value={from} onInput={(e) => setFrom(e.currentTarget.value)}
              class="rounded-md border border-border-strong bg-bg-base px-2 py-1.5 text-sm text-text-body" />
            <span class="text-text-muted">→</span>
            <input type="date" value={to} onInput={(e) => setTo(e.currentTarget.value)}
              class="rounded-md border border-border-strong bg-bg-base px-2 py-1.5 text-sm text-text-body" />
          </div>
        )}
      </div>

      {ovError && (
        <div class="mb-6 rounded-md border border-border-strong bg-bg-card p-3 text-sm text-text-secondary">
          Couldn’t load statistics — {ovError.message || 'request failed'}
          {ovError.status ? ` (${ovError.status})` : ''}.
          {ovError.status === 401 && ' Your session may have expired — try logging in again.'}
        </div>
      )}

      {/* KPI grid */}
      <section class="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Issues opened" kpi={w?.issuesOpened} invert />
        <Kpi label="Issues resolved" kpi={w?.issuesResolved} />
        <Kpi label="Units consumed" kpi={w?.unitsConsumed} invert />
        <Kpi label="Mixes saved" kpi={w?.mixesSaved} />
        <Kpi label="Inks added" kpi={w?.inksAdded} />
        <Kpi label="Events completed" kpi={w?.eventsCompleted} />
        <Kpi label="Events missed" kpi={w?.eventsMissed} invert />
        <StatCard label="Open issues now" value={s?.openIssues ?? '—'} accent="#E63B7A" />
      </section>

      {/* Point-in-time scalars */}
      <section class="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="MTTR" value={s?.mttrHours ?? '—'} suffix="hrs" accent={INK} />
        <StatCard label="Schedule adherence" value={s?.scheduleAdherence ?? '—'} suffix="%" accent={INK} />
        <StatCard label="Low stock" value={s?.lowStock ?? '—'} accent="#F4C430" />
        <StatCard label="Avg match ΔE" value={s?.avgMixDeltaE ?? '—'} accent="#00B4D8" />
      </section>

      <Section title="Maintenance">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TrendCard title="Issues opened vs resolved" qs={qs} metrics={[
            { metric: 'issues_opened', color: '#E63B7A', label: 'Opened' },
            { metric: 'issues_resolved', color: '#7BD4A8', label: 'Resolved' },
          ]} />
          <BreakdownCard title="Issues by printer" qs={qs} dimension="printer" metric="issues" accent="#E63B7A" />
          <BreakdownCard title="Issues by severity" qs={qs} dimension="severity" metric="issues" accent="#E63B7A" />
          <RecurringCard qs={qs} />
        </div>
      </Section>

      <Section title="Inventory">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TrendCard title="Units consumed" qs={qs} metrics={[{ metric: 'inventory_consumed', color: '#F4C430', label: 'Consumed' }]} />
          <BreakdownCard title="Top consumed items" qs={qs} dimension="inventory_item" metric="consumption" accent="#F4C430" />
          <BreakdownCard title="Consumption by category" qs={qs} dimension="inventory_category" metric="consumption" accent="#F4C430" />
        </div>
      </Section>

      <Section title="Ink Books">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TrendCard title="Mixes saved" qs={qs} metrics={[{ metric: 'mixes_saved', color: '#00B4D8', label: 'Mixes' }]} />
          <BreakdownCard title="Mixes by printer" qs={qs} dimension="printer" metric="mixes" accent="#00B4D8" />
          <BreakdownCard title="Mixes by print direction" qs={qs} dimension="print_direction" metric="mixes" accent="#00B4D8" />
          <BreakdownCard title="Inks by customer" qs={qs} dimension="customer" metric="inks" accent="#FF8A4C" />
        </div>
      </Section>

      <Section title="Compare">
        <ComparePanel qs={qs} />
      </Section>
    </div>
  );
};
